import type { HTMLChakraProps } from '@chakra-ui/react';
import type { ComponentType } from 'react';

import { type ColorScale, generateColorScale } from '@revelio/styles';

export type { ColorScale } from '@revelio/styles';

/**
 * A color can be specified as either:
 * - A single hex string (e.g. `'#4285F4'`) — a full 10-shade scale is generated automatically
 * - A full `ColorScale` object for precise control over each shade
 */
export type ColorInput = string | ColorScale;

export type BrandLogos = {
  /** 45x45 sidebar icon component */
  sidebarLogo: ComponentType;
  /** Full text logo component (login page, mobile unsupported page) */
  fullLogo: ComponentType<HTMLChakraProps<'svg'>>;
  /** Static logo image URL for report cover pages */
  reportLogo: string;
  /** Watermark image URL for PNG exports */
  watermark: string;
  /** Favicon URL */
  favicon: string;
};

export type BrandConfig = {
  /** Identifier matching User.client_name from GraphQL */
  clientName: string;
  /** Display name shown in page title */
  displayName: string;
  /** Primary accent color — a single hex string or a full ColorScale */
  primaryColor: ColorInput;
  /** Sidebar background color — a single hex string or a full ColorScale */
  sidebarColor: ColorInput;
  /** Logo components and asset URLs */
  logos: BrandLogos;
  /**
   * Custom chart color palette — an ordered list of hex colors used for
   * data series in charts (bars, lines, areas, etc.).
   *
   * When omitted, the default Revelio palette is used.
   */
  chartColors?: string[];
  /**
   * Color used for generic/residual categories like the "Other" bucket
   * in breakdown charts. Kept separate from `chartColors` so ordered
   * categorical bars can consume the palette in order without "Other"
   * stealing a slot.
   *
   * When omitted, the default Revelio "Other" gray (#A0A9B8) is used.
   */
  otherColor?: string;
  /**
   * Dark-mode chart palette. When omitted, falls back to `chartColors`, then
   * the default Revelio dark palette.
   */
  darkChartColors?: string[];
  /**
   * Palette for breakdown/segmentation charts (stacked & grouped bars). When
   * omitted, inherits `chartColors`, then the default Revelio breakdown palette.
   */
  breakdownColors?: string[];
  /**
   * Dark-mode breakdown palette. When omitted, falls back to `breakdownColors`,
   * `darkChartColors`, `chartColors`, then the Revelio breakdown palette.
   */
  darkBreakdownColors?: string[];
  /**
   * Dark-mode "Other"/residual color. When omitted, falls back to `otherColor`,
   * then the default Revelio dark gray (#808793).
   */
  darkOtherColor?: string;
  /**
   * Dark-neutral color used for UI chrome text (body copy, titles) and
   * tooltip backgrounds. Provide a single hex to auto-generate a scale,
   * or a full `ColorScale` for precise control.
   *
   * Brands whose `primaryColor` is a vibrant accent (not a dark text
   * color) should set this explicitly to a near-black or dark neutral
   * that reads as "default text" on white.
   *
   * When omitted, the default Revelio navy scale is used.
   */
  textColor?: ColorInput;
  /**
   * Path or URL to a custom loading animation GIF shown by the branded
   * Loading wrapper. When omitted, a Chakra Spinner is used instead.
   */
  loadingGif?: string;
  /**
   * Portrait cover image used by the dashboard report cover page (HTML
   * render). SVG is recommended here — it stays crisp at any device-pixel
   * ratio in the browser. Used by the PDF and PPT exporters as a fallback
   * when `coverImagePortraitPpt` is not set.
   *
   * Suggested aspect: 7.5×10 portrait, matching the dashboard PDF page.
   */
  coverImagePortrait?: string;
  /**
   * Optional PNG override for the PDF cover and the Build-a-Dash report
   * PPT cover slide (portrait, 7.5"×10"). Provide this when
   * `coverImagePortrait` is an SVG and you want a PNG raster instead — it
   * is more reliable in jsPDF's canvas-rasterization path and renders
   * consistently across PowerPoint / Google Slides / Keynote (which can
   * render SVG poorly outside modern PowerPoint / Microsoft 365). When
   * unset, the exporters fall back to `coverImagePortrait`.
   *
   * Suggested aspect: 3:4 portrait (e.g. 1224×1632 or 2550×3300 PNG).
   */
  coverImagePortraitPpt?: string;
  /**
   * Background color behind the cover image, used to fill any gaps when the
   * cover image aspect ratio doesn't match the container. Should match
   * the dominant background color of the cover image.
   */
  coverBackgroundColor?: string;
  /**
   * Landscape cover image used for PowerPoint exports generated from the
   * single-chart / Custom Dash flow. Those slides are landscape (default
   * 10:5.625), so the asset should be landscape. When provided, replaces
   * the default cover slide background.
   *
   * Suggested aspect: 16:9 (e.g. 3840×2160 PNG).
   */
  coverImageLandscape?: string;
  /**
   * Color used for the report title and parameter labels on the report
   * cover (PDF and PowerPoint). Provide a near-black for light cover
   * backgrounds; the default white reads against dark cover backgrounds.
   *
   * Default: `#FFFFFF`
   */
  coverTitleColor?: string;
  /**
   * Color used for the report description and parameter values on the
   * report cover (PDF and PowerPoint). Defaults to a soft grey that reads
   * against dark cover backgrounds; override for light cover backgrounds.
   *
   * Default: `#BABFC9`
   */
  coverDescriptionColor?: string;
  /**
   * Font face used for all text in the PowerPoint export (chart titles,
   * labels, footers, closing slide). PowerPoint falls back gracefully if a
   * viewer lacks the font; it is not embedded. When omitted, Arial is used.
   */
  pptxFontFace?: string;
  /**
   * When set, the PPT exporter renders this branded closing slide INSTEAD of
   * the default Revelio "About / Contact" info slide — for both the
   * landscape (single/multi chart) and portrait (report) exports.
   */
  closingSlide?: {
    /** Logo + tagline lockup image, centred on the slide. PNG recommended. */
    logoImage: string;
    /** Slide background fill hex, e.g. `'#91ebc9'`. */
    backgroundColor: string;
    /** Renders "© {year}, {copyrightHolder}. All rights reserved." */
    copyrightHolder: string;
  };
};

/** Resolved brand config where colors are always full scales. */
export type ResolvedBrandConfig = Omit<
  BrandConfig,
  | 'primaryColor'
  | 'sidebarColor'
  | 'textColor'
  | 'coverTitleColor'
  | 'coverDescriptionColor'
> & {
  primaryColor: ColorScale;
  sidebarColor: ColorScale;
  textColor: ColorScale;
  coverTitleColor: string;
  coverDescriptionColor: string;
};

function resolveColor(color: ColorInput): ColorScale {
  return typeof color === 'string' ? generateColorScale(color) : color;
}

/** Default Revelio navy scale, used as fallback UI chrome text color. */
const DEFAULT_TEXT_COLOR_SCALE: ColorScale = {
  50: '#E6EBF4',
  100: '#CDD7EA',
  200: '#9BAFD4',
  300: '#6987BF',
  400: '#43639E',
  500: '#2D426A',
  600: '#243656',
  700: '#1B2840',
  800: '#121B2B',
  900: '#090D15',
};

const DEFAULT_COVER_TITLE_COLOR = '#FFFFFF';
const DEFAULT_COVER_DESCRIPTION_COLOR = '#BABFC9';

const brandRegistry = new Map<string, ResolvedBrandConfig>();

/**
 * Register a brand configuration. Call at app startup before rendering.
 * The first brand registered becomes the default fallback.
 *
 * Colors can be provided as either a single hex string or a full scale:
 * ```ts
 * registerBrand('acme', {
 *   clientName: 'acme',
 *   displayName: 'Acme Corp',
 *   primaryColor: '#FF6B35',   // auto-generates full scale
 *   sidebarColor: '#1A1A2E',   // auto-generates full scale
 *   logos: { ... },
 * });
 * ```
 */
export function registerBrand(key: string, config: BrandConfig) {
  brandRegistry.set(key, {
    ...config,
    primaryColor: resolveColor(config.primaryColor),
    sidebarColor: resolveColor(config.sidebarColor),
    textColor: config.textColor
      ? resolveColor(config.textColor)
      : DEFAULT_TEXT_COLOR_SCALE,
    coverTitleColor: config.coverTitleColor ?? DEFAULT_COVER_TITLE_COLOR,
    coverDescriptionColor:
      config.coverDescriptionColor ?? DEFAULT_COVER_DESCRIPTION_COLOR,
  });
}

/**
 * Look up a brand config by client_name.
 * Falls back to the 'default' brand if no match is found.
 */
export function getBrandConfig(
  clientName: string | null | undefined
): ResolvedBrandConfig {
  if (clientName && brandRegistry.has(clientName)) {
    // biome-ignore lint/style/noNonNullAssertion: migrated from develop
    return brandRegistry.get(clientName)!;
  }
  const defaultBrand = brandRegistry.get('default');
  if (!defaultBrand) {
    throw new Error(
      'getBrandConfig: no default brand registered. Call registerBrand("default", ...) before using the brand system.'
    );
  }
  return defaultBrand;
}
